import { Component, EventEmitter, inject, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { Table, TableComponent } from '@markant-platform/shared/table/feature';
import { EMPTY_PAGE, TableDraft, TableDraftBuilder } from '@markant-platform/shared/table/model';
import { AuthenticatedUserService, UserService } from '@markant-platform/shared/user/data-access';
import { SnackbarService } from '@markant-platform/shared/snackbar/feature';
import { Page, TableContext } from '@markant-platform/shared/data-access';
import { catchError, distinctUntilChanged, of, switchMap, tap } from 'rxjs';
import { OrganizationUserDTO } from '@markant-platform/shared/user/model';
import { deepEqual } from '@markant-platform/shared/util-helpers';
import { buildFilterQueryParameter } from '@markant-platform/shared/ui-filter';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { AsyncPipe, NgIf } from '@angular/common';
import { MarkantElementsModule } from '@markant/elements-angular';
import { TranslocoModule, TranslocoPipe } from '@jsverse/transloco';
import { FormControlErrorPipe, FormControlKeyErrorPipe } from '@markant-platform/shared/util-pipes';
import {
  DEFAULT_FILTER,
  DEFAULT_SORT,
  SELECT_TEMPLATE_USER_TABLE_CELLS,
} from './configuration/organization-member-overview.config';

@Component({
  selector: 'markant-platform-organization-member-select-template-user-dialog',
  templateUrl: './organization-member-select-template-user.component.html',
  styleUrls: ['./organization-member-select-template-user.component.scss'],
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MarkantElementsModule,
    TableComponent,
    AsyncPipe,
    NgIf,
    TranslocoModule,
    FormControlKeyErrorPipe,
    FormControlErrorPipe,
    TranslocoPipe,
  ],
})
export class OrganizationMemberSelectTemplateUserComponent extends Table implements OnInit, OnChanges {
  @Input({ required: true }) organizationId!: string;

  @Input({ required: true }) type?: 'GROUP' | 'SERVICE';
  @Input() showResetConfirmationPrompt = true;

  @Output() closed = new EventEmitter<void>();
  @Output() updateSelection = new EventEmitter<{ selectedValue: string; closeDialog: boolean }>();

  translocoPath = '';
  override totalCount = 0;
  override preSelectedOptions = DEFAULT_FILTER;
  override tableDraft: TableDraft = new TableDraftBuilder(SELECT_TEMPLATE_USER_TABLE_CELLS)
    .disableRowLink(true)
    .radiobuttons()
    .sortableColumns(['firstname', 'lastname'])
    .sort(DEFAULT_SORT)
    .build();

  userRoles: string[] = [];
  userId?: string;
  selected = '';

  private readonly userService: UserService = inject(UserService);
  private readonly authenticatedUserService: AuthenticatedUserService = inject(AuthenticatedUserService);
  private readonly snackBarService: SnackbarService = inject(SnackbarService);

  constructor() {
    super(TableContext.ORGANIZATION_MEMBER_SELECT_TEMPLATE_USER);
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.userId = this.authenticatedUserService.userId;

    this.tableParams$
      .pipe(
        distinctUntilChanged(deepEqual),
        tap(({ sort }) => {
          this.isLoading$.next(true);
          this.tableDraft.sortStartColumnId = sort.sortField;
          this.tableDraft.sortStartDirection = sort.sortOrder;
        }),
        switchMap(({ page: { index, size }, activeFilter, searchQuery, sort }) =>
          this.userService.getUsersInOrganizationAsOrganizationUserDto(
            this.organizationId,
            index - 1,
            size,
            sort,
            searchQuery,
            buildFilterQueryParameter(['state'], activeFilter),
            true
          )
        ),
        takeUntilDestroyed(this.destroyRef),
        catchError(() => {
          this.snackBarService.showErrorSnackbarWithTransloco('common.insufficientRolesError');
          return of(EMPTY_PAGE as Page<OrganizationUserDTO>);
        })
      )
      .subscribe(({ result, totalCount }) => {
        const tableData = this.tableService.returnMappedTableData(result, this.tableDraft);
        this.tableData$.next(tableData);
        this.totalCount = totalCount;

        this.isLoading$.next(false);
      });
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['type']) {
      this.translocoPath =
        this.type === 'GROUP'
          ? 'organization.users.invite.groupView.useTemplate.'
          : 'organization.users.invite.cartridgeView.useTemplate.';
    }
  }

  onSelectionChange(selected: string): void {
    this.selected = selected;
  }

  confirmSelection(): void {
    if (this.selected) {
      this.updateSelection.emit({ selectedValue: this.selected, closeDialog: true });
      return;
    }
    this.snackBarService.showErrorSnackbarWithTransloco(
      'organization.users.invite.groupView.useTemplate.noSelectionError'
    );
  }

  closeDialog(): void {
    this.closed.emit();
  }

  confirmResetConfiguration(): void {
    this.showResetConfirmationPrompt = false;
    this.updateSelection.emit({ selectedValue: '', closeDialog: false });
  }
}
